import { skipToken } from '@apollo/client/react'
import { Button, Chip, MenuItem } from '@mui/material'
import { type FC } from 'react'
import { useNavigate } from 'react-router'
import IcBaselineAdminPanelSettings from '~icons/ic/baseline-admin-panel-settings'
import IcBaselineBlock from '~icons/ic/baseline-block'
import { dialogInspection } from 'src/components/Modal/InspectDialog'
import {
  executeOpBanUser,
  executeOpRemoveCommentTurnstile,
  executeOpRequireCommentTurnstile,
  executeOpResetUserAvatar,
  executeOpResetUserCover,
  executeOpShadowBanUser,
  executeOpUnBanUser,
} from 'src/services/apollo/adminOp'
import {
  useGetUserWithExternalProfiles,
  useMyInfo,
} from 'src/services/apollo/auth'
import type { User } from 'src/services/apollo/generated/graphql'
import DropdownMenu from '../DropdownMenu'
import { opAction } from './util'

const UserOpActions: FC<{ user: User }> = props => {
  const { data: me } = useMyInfo()
  const { data: userWithFullinfo } = useGetUserWithExternalProfiles(
    me?.isAdmin ? props.user.id : skipToken,
  )
  const navigate = useNavigate()

  if (import.meta.env.PROD && !me?.isAdmin) return null

  const user = userWithFullinfo ?? props.user
  const isUserBanned = user.username.startsWith('banned-user')

  const userDetail = (
    <div>
      <div>user id: {user.id}</div>
      <div>username: {user.username}</div>
      <div>nickname: {user.displayName}</div>
      <div>email: {user.email}</div>
    </div>
  )

  const banUser = () =>
    opAction(
      async () => {
        await executeOpBanUser({ id: user.id })
        navigate(`/@banned-user-${user.id}`)
      },
      <div>
        <div className="py-4">Are you sure to ban this user?</div>
        {userDetail}
      </div>,
    )

  const unbanUser = () =>
    opAction(
      async () => {
        await executeOpUnBanUser({ id: user.id })
        navigate(`/@user-${user.id}`)
      },
      <div>
        <div className="py-4">Are you sure to unban this user?</div>
        {userDetail}
      </div>,
    )

  const shadowBanUser = () =>
    opAction(
      async () => {
        await executeOpShadowBanUser({ id: user.id })
      },
      <div>
        <div className="py-4">Are you sure to shadow ban this user?</div>
        {userDetail}
      </div>,
    )

  const requireCommentTurnstile = () =>
    opAction(
      async () => {
        await executeOpRequireCommentTurnstile({ id: user.id })
      },
      <div>
        <div className="py-4">
          Are you sure to require comment turnstile for this user? This user
          will need to pass Turnstile verification every time they post a
          comment.
        </div>
        {userDetail}
      </div>,
    )

  const removeCommentTurnstile = () =>
    opAction(
      async () => {
        await executeOpRemoveCommentTurnstile({ id: user.id })
      },
      <div>
        <div className="py-4">
          Are you sure to remove comment turnstile requirement for this user?
        </div>
        {userDetail}
      </div>,
    )

  const resetUserAvatar = () =>
    opAction(
      async () => {
        await executeOpResetUserAvatar({ userId: user.id })
      },
      <div>
        <div className="py-4">
          Are you sure to reset the avatar of this user? A notification will be
          sent to the user.
        </div>
        {userDetail}
      </div>,
    )

  const resetUserCover = () =>
    opAction(
      async () => {
        await executeOpResetUserCover({ userId: user.id })
      },
      <div>
        <div className="py-4">
          Are you sure to reset the cover of this user? A notification will be
          sent to the user.
        </div>
        {userDetail}
      </div>,
    )

  const inspectUser = () => dialogInspection(user)

  return (
    <>
      {isUserBanned && (
        <Chip
          icon={<IcBaselineBlock />}
          color="error"
          label="Banned"
          variant="outlined"
        />
      )}

      <DropdownMenu
        anchor={
          <Button
            startIcon={<IcBaselineAdminPanelSettings />}
            variant="outlined"
            color="warning"
            className="rounded-md"
          >
            Admin
          </Button>
        }
      >
        <MenuItem onClick={inspectUser}>
          <span className="normal-case">inspect</span>
        </MenuItem>

        <MenuItem
          className="bg-red-100 text-red-500 hover:bg-red-200 transition duration-75"
          onClick={banUser}
          disabled={isUserBanned}
        >
          <span className="normal-case">ban this user</span>
        </MenuItem>

        <MenuItem
          className="bg-green-100 text-green-500 hover:bg-green-200 transition duration-75"
          onClick={unbanUser}
          disabled={!isUserBanned}
        >
          <span className="normal-case">unban this user</span>
        </MenuItem>

        <MenuItem
          className="bg-red-100 text-red-500 hover:bg-red-200 transition duration-75"
          onClick={shadowBanUser}
        >
          <span className="normal-case">shadow ban this user</span>
        </MenuItem>

        <MenuItem
          className="bg-orange-100 text-orange-500 hover:bg-orange-200 transition duration-75"
          onClick={requireCommentTurnstile}
        >
          <span className="normal-case">require comment turnstile</span>
        </MenuItem>

        <MenuItem
          className="bg-green-100 text-green-500 hover:bg-green-200 transition duration-75"
          onClick={removeCommentTurnstile}
        >
          <span className="normal-case">remove comment turnstile</span>
        </MenuItem>

        <MenuItem
          className="bg-red-100 text-red-500 hover:bg-red-200 transition duration-75"
          onClick={resetUserAvatar}
        >
          <span className="normal-case">reset user avatar</span>
        </MenuItem>

        <MenuItem
          className="bg-red-100 text-red-500 hover:bg-red-200 transition duration-75"
          onClick={resetUserCover}
        >
          <span className="normal-case">reset user cover</span>
        </MenuItem>
      </DropdownMenu>
    </>
  )
}

export default UserOpActions
